import { useState } from "react";
import { NavLink, Link, Outlet, useLocation } from "react-router-dom";
import { Home, FolderKanban, Users, Images, ListChecks, Settings, PanelLeftClose, PanelLeftOpen, Menu, ShieldCheck, LogOut } from "lucide-react";
import VeyraLogo, { VeyraMark } from "@/components/brand/VeyraLogo";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useStudio } from "@/store/StudioContext";
import { useAuth } from "@/store/AuthContext";
import { isUserOwner } from "@/pages/AdminPage";
import { t } from "@/i18n";

// Iniciales del propietario para el avatar de la tarjeta de perfil. El nombre
// viene siempre de la configuración (Ajustes → Mi perfil), nunca hardcodeado.
const getInitials = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const NavItems = ({ collapsed, onNavigate, totalJobCount, nav }) => (
  <nav className="flex flex-col gap-1 px-2" aria-label="Principal">
    {nav.map(({ to, icon: Icon, label, id, end }) => (
      <NavLink
        key={id}
        to={to}
        end={end}
        onClick={onNavigate}
        data-testid={`nav-${id}`}
        title={label}
        className={({ isActive }) =>
          `group relative flex h-10 items-center gap-3 rounded-lg px-3 text-sm font-medium transition-colors ${isActive ? "bg-[#222] text-[#f4f1ec]" : "text-[#a3a3a3] hover:bg-[#1b1b1b] hover:text-[#f4f1ec]"}`
        }
      >
        {({ isActive }) => (
          <>
            {isActive && <span className="absolute left-0 top-2 h-6 w-[3px] rounded-r bg-[#f5b544]" />}
            <Icon size={18} className="shrink-0" />
            {!collapsed && <span className="truncate">{label}</span>}
            {id === "tasks" && totalJobCount > 0 && !collapsed && (
              <span data-testid="nav-tasks-count" title={t.tasks.countHint} className="ml-auto rounded-full bg-[#2a2a2a] px-1.5 font-mono text-[10px] text-[#f5b544]">{totalJobCount}</span>
            )}
          </>
        )}
      </NavLink>
    ))}
  </nav>
);

const SidebarBody = ({ collapsed, onToggle, onNavigate }) => {
  const { state } = useStudio();
  const { user, logout } = useAuth();
  const isOwner = isUserOwner(user);
  const [loggingOut, setLoggingOut] = useState(false);
  // Badge: solo tareas activas (running + pending), no total
  const totalJobCount = (state.jobs || []).filter(
    (j) => j.status === "running" || j.status === "pending"
  ).length;
  const ws = state.onboarding?.workspaceName;
  const displayName = user?.name || (isOwner ? state.onboarding?.ownerName : null) || user?.email || t.common.notConfigured;
  const roleLabel = isOwner ? (t.settings?.ownerRoleLabel || "Propietario") : (t.settings?.guestRoleLabel || "Invitado");
  const initials = getInitials(displayName);

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
      if (onNavigate) onNavigate();
    } finally {
      setLoggingOut(false);
    }
  };

  const nav = [
    { to: "/", icon: Home, label: t.nav.home, id: "home", end: true },
    { to: "/projects", icon: FolderKanban, label: t.nav.projects, id: "projects" },
    { to: "/characters", icon: Users, label: t.nav.characters, id: "characters" },
    { to: "/gallery", icon: Images, label: t.nav.gallery, id: "gallery" },
    { to: "/tasks", icon: ListChecks, label: t.nav.tasks, id: "tasks" },
    ...(isOwner ? [{ to: "/admin", icon: ShieldCheck, label: t.nav.admin || "Panel Administrativo", id: "admin" }] : []),
    { to: "/settings", icon: Settings, label: t.nav.settings, id: "settings" },
  ];

  return (
    <div className="flex h-full flex-col">
      <div className={`flex h-16 items-center ${collapsed ? "justify-center" : "px-4"}`}>
        <Link to="/" data-testid="sidebar-logo-link" title={t.nav.home_icon_hint} className="flex items-center" onClick={onNavigate}>
          {collapsed ? <VeyraMark size={26} /> : <VeyraLogo size={26} />}
        </Link>
      </div>
      {!collapsed && (
        <div className="mb-3 px-5">
          <div className="v-eyebrow">{t.nav.workspace}</div>
          <div data-testid="sidebar-workspace-name" className="truncate text-sm font-medium">{ws || t.common.notConfigured}</div>
        </div>
      )}
      <NavItems collapsed={collapsed} onNavigate={onNavigate} totalJobCount={totalJobCount} nav={nav} />
      <div className="mt-auto flex flex-col gap-2 p-3">
        <Link
          to="/settings#profile"
          onClick={onNavigate}
          data-testid="sidebar-profile-link"
          title={collapsed ? `${displayName} · ${roleLabel}` : undefined}
          className={`flex min-h-[44px] items-center gap-2.5 rounded-lg border border-[#303030] bg-[#151515] px-3 py-2 hover:border-[#4a4a4a] ${collapsed ? "justify-center" : ""}`}
        >
          <span data-testid="sidebar-profile-avatar" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f5b544] font-mono text-xs font-semibold text-[#1a1206]">{initials}</span>
          {!collapsed && (
            <div className="min-w-0">
              <div data-testid="sidebar-profile-name" className="truncate text-sm font-medium">{displayName}</div>
              <div className="text-[10px] uppercase tracking-wide text-[#a3a3a3]">{roleLabel}</div>
            </div>
          )}
        </Link>
        <button
          type="button"
          data-testid="sidebar-logout"
          onClick={handleLogout}
          disabled={loggingOut}
          title={t.auth?.logout || "Cerrar sesión"}
          aria-label={t.auth?.logout || "Cerrar sesión"}
          className={`group flex min-h-[44px] items-center gap-2.5 rounded-lg border border-[#303030] bg-[#151515] px-3 py-2 text-xs font-medium text-[#a3a3a3] transition-colors hover:border-[#4a4a4a] hover:bg-[#1a1a1a] hover:text-[#f4f1ec] disabled:opacity-50 ${collapsed ? "justify-center" : ""}`}
        >
          <LogOut size={collapsed ? 16 : 14} className="shrink-0 text-[#888] transition-colors group-hover:text-[#f4f1ec]" />
          {!collapsed && (
            <span className="truncate">
              {loggingOut ? (t.auth?.loggingOut || "Cerrando sesión...") : (t.auth?.logout || "Cerrar sesión")}
            </span>
          )}
        </button>
        {onToggle && (
          <button
            data-testid="sidebar-toggle"
            onClick={onToggle}
            title={collapsed ? t.nav.expand : t.nav.collapse}
            aria-label={collapsed ? t.nav.expand : t.nav.collapse}
            className={`hidden items-center gap-2 rounded-lg border border-[#303030] px-3 py-2 text-xs font-medium text-[#a3a3a3] transition-colors hover:bg-[#222] hover:text-[#f4f1ec] md:flex ${collapsed ? "justify-center" : "justify-center"}`}
          >
            {collapsed ? <PanelLeftOpen size={16} /> : <><PanelLeftClose size={16} />{t.nav.collapse}</>}
          </button>
        )}
      </div>
    </div>
  );
};

export default function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { state } = useStudio();
  const { user } = useAuth();
  const isOwner = isUserOwner(user);
  const displayName = user?.name || (isOwner ? state?.onboarding?.ownerName : null) || user?.email || t.common.notConfigured;
  const roleLabel = isOwner ? (t.settings?.ownerRoleLabel || "Propietario") : (t.settings?.guestRoleLabel || "Invitado");
  const initials = getInitials(displayName);
  const location = useLocation();
  const inProject = /^\/projects\/[^/]+/.test(location.pathname);

  return (
    <div className="flex h-full min-h-screen bg-[#101010] text-[#f4f1ec]">
      <aside data-testid="sidebar" className={`relative hidden shrink-0 border-r border-[#303030] bg-[#141414] transition-[width] duration-200 md:block ${collapsed ? "w-16" : "w-60"}`}>
        <SidebarBody collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 border-[#303030] bg-[#141414] p-0 text-[#f4f1ec]">
          <SheetTitle className="sr-only">{t.nav.menu}</SheetTitle>
          <SidebarBody collapsed={false} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center justify-between border-b border-[#303030] bg-[#141414] px-4 md:hidden">
          <div className="flex items-center gap-3">
            <button
              data-testid="mobile-menu-button"
              onClick={() => setMobileOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-[#a3a3a3] transition-colors hover:bg-[#222] hover:text-[#f4f1ec]"
              aria-label={t.nav.menu}
              title={t.nav.menu}
            >
              <Menu size={22} />
            </button>
            <VeyraLogo size={22} />
          </div>
          <Link
            to="/settings#profile"
            data-testid="mobile-header-avatar"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f5b544] font-mono text-xs font-semibold text-[#1a1206]"
            title={`${displayName} · ${roleLabel}`}
          >
            {initials}
          </Link>
        </header>
        <main className={`min-w-0 flex-1 ${inProject ? "flex flex-col overflow-y-auto md:overflow-hidden" : "overflow-y-auto"}`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
